import { ReactNode, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { HippoComponentProps } from '../index';
import React from 'react';

import styles from './Dialog.module.scss';
import classNames from 'classnames';
import TextButton from '../TextButton/TextButton';
import Button from '../Button/Button';
import IconButton from '../IconButton/IconButton';

export interface Props extends HippoComponentProps {
  isOpen: boolean;
  width?: number;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  closable?: boolean;
  alternativeClose?: boolean; // allow closing the dialog by clicking outside or pressing the escape key
  contextMenu?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
}

const Dialog: React.FC<Props> = ({
  dataQA,
  className,
  isOpen,
  width,
  title,
  children,
  footer,
  closable,
  alternativeClose = true,
  contextMenu,
  onClose,
  onConfirm,
}) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (isOpen && event.key === 'Escape' && alternativeClose) {
        onClose?.();
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  function handleBackdropClick(event: React.MouseEvent) {
    if (
      (event.target as HTMLElement).classList.contains(styles.backdrop) &&
      alternativeClose
    ) {
      onClose?.();
    }
  }

  const defaultFooter = (
    <div className={styles.defaultFooter}>
      <TextButton
        dataQA="cancel-button"
        size="regular"
        text="Cancel"
        type="primary"
        onClick={onClose}
      />
      <Button
        dataQA="confirm-button"
        size="regular"
        style="filled"
        text="Confirm"
        type="primary"
        onClick={onConfirm}
      />
    </div>
  );

  const modalContent = isOpen && (
    <>
      <div className={styles.backdrop} onClick={handleBackdropClick} />
      <div
        data-qa={dataQA}
        className={classNames(styles.container, className)}
        style={{ width }}
      >
        {((title && title.length) || contextMenu || closable) && (
          <div className={styles.header}>
            {title && title.length && (
              <div className={styles.title}>{title}</div>
            )}
            <div className={styles.iconControls}>
              {contextMenu && (
                <div className={styles.iconFrame}>
                  <IconButton
                    dataQA="context-button"
                    icon="more_vert"
                    iconSize={24}
                    size="regular"
                    type="primary"
                  />
                </div>
              )}
              {closable && (
                <div className={styles.iconFrame}>
                  <IconButton
                    dataQA="close-button"
                    icon="close"
                    iconSize={24}
                    size="regular"
                    type="primary"
                    onClick={onClose}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        <div className={styles.content}>{children}</div>
        <div className={styles.footer}>{footer || defaultFooter}</div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

export default Dialog;
